import {computed, signal} from "@angular/core"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {AutoTilingPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/auto-tiling/panel/auto-tiling-panel.component"
import {assertNever, deepCopy} from "@cm/utils"
import {
    Operator,
    OperatorExecutionMode,
    OperatorFlags,
    OperatorPanelComponentType,
    OperatorProcessingHints,
} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {TilingAutoGenerateToolbox} from "@app/textures/texture-editor/operator-stack/operators/auto-tiling/toolbox/auto-tiling-toolbox"
import * as Tiling from "@app/textures/texture-editor/operator-stack/operators/auto-tiling/service/auto-tiling.service"
import {TextureType} from "@generated"
import {takeUntil} from "rxjs"
import {getSimpleJobState, SimpleJobState} from "@app/textures/utils/simple-job-state"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {AnyImageOpResultNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeDataObject} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/data-object/node"
import {AutoTilingState, AutoTilingViewMode} from "@app/textures/texture-editor/operator-stack/operators/auto-tiling/operator-auto-tiling-types"

export {AutoTilingState, AutoTilingViewMode} from "../../../operator-stack/operators/auto-tiling/operator-auto-tiling-types"

export class OperatorAutoTiling extends OperatorBase<TextureEditNodes.OperatorAutoTiling> {
    // OperatorBase
    override readonly flags = new Set<OperatorFlags>(["no-clone", "no-disable", "apply-to-all-texture-types"])

    readonly panelComponentType: OperatorPanelComponentType = AutoTilingPanelComponent

    readonly type = "operator-auto-tiling" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorAutoTiling | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-auto-tiling",
                enabled: true,
                mode: "tiling",
                area: undefined,
                hints: [],
                params: {
                    processingMode: "cloud",
                    blendingBorder: 100,
                    maxBlendingRadius: 256,
                    patternType: "large",
                    featureWeighting: "normal_diffuse",
                    gradientCorrection: true,
                    filterMode: "normal",
                    filterRadius: 4,
                    alignIndividualMaps: false,
                    edgeAlignmentWindowSize: 0,
                    edgeAlignmentSmoothingIterations: 15,
                },
                jobTaskId: undefined,
                outputMaps: undefined,
            },
        )

        this.tilingService = callback.injector.get(Tiling.AutoTilingService)

        if (this.callback.textureEditorData.textureSetRevisionSpecific.textureSetRevision.editsJson?.schema <= 3 && this.node.hints.length > 0) {
            this._$autoTilingState.set(AutoTilingState.Complete) // this is an old pre-texture-set-revision auto-tiling which by definition is complete
        } else if (this.node.jobTaskId === undefined && this.node.outputMaps === undefined) {
            this._$autoTilingState.set(AutoTilingState.Setup) // there is no auto-tiling job task given which means we are in the setup phase
        } else {
            if (this.callback.textureEditorData.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob()?.id !== this.node.jobTaskId) {
                if (!this.node.outputMaps) {
                    throw Error("INTERNAL ERROR: Output maps must be set if the job task id is not the current job task id")
                }
                this._$autoTilingState.set(AutoTilingState.Complete) // must be complete as the current job task is not the auto-tiling job task
            } else {
                // the current job task is the auto-tiling job task
                const jobState = this.callback.textureEditorData.textureSetRevisionSpecific.textureSetRevision.$editsProcessingJob()?.state
                if (!jobState) {
                    this._$autoTilingState.set(AutoTilingState.Setup)
                } else {
                    const simpleJobState = getSimpleJobState(jobState)
                    switch (simpleJobState) {
                        case SimpleJobState.Failed:
                        case SimpleJobState.Cancelled:
                            this._$autoTilingState.set(AutoTilingState.Failed)
                            break
                        case SimpleJobState.InProgress:
                            this._$autoTilingState.set(AutoTilingState.InProgress)
                            break
                        case SimpleJobState.Complete:
                            this._$autoTilingState.set(AutoTilingState.Complete)
                            break
                        default:
                            assertNever(simpleJobState)
                    }
                }
            }
        }

        this._$viewMode.set(this.$autoTilingState() === AutoTilingState.Complete ? AutoTilingViewMode.Result : AutoTilingViewMode.Setup)
        this.addTilingHints()
        this._canvasToolbox = new TilingAutoGenerateToolbox(this)

        this.callback.selectedOperatorChanged.pipe(takeUntil(this.unsubscribe)).subscribe((operator) => {
            if (operator === this) {
                // make sure we don't display the setup view if the setup cannot be changed anymore
                if (this.$viewMode() === AutoTilingViewMode.Setup && !this.$canChangeAutoTilingSetup()) {
                    this.setViewMode(AutoTilingViewMode.Result)
                }
                this.updateToolbox()
            }
        })
        this.updateToolbox()
    }

    // OperatorBase
    get canvasToolbox(): TilingAutoGenerateToolbox | null {
        return this.$viewMode() === AutoTilingViewMode.Setup ? this._canvasToolbox : null
    }

    // OperatorBase
    override dispose(): void {
        super.dispose()
        this._canvasToolbox.remove()
    }

    // OperatorBase
    async clone(): Promise<Operator> {
        throw Error("Auto tiling operator cannot be cloned")
    }

    // OperatorBase
    protected override async createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        const state = this.$autoTilingState()
        switch (state) {
            case AutoTilingState.Setup:
            case AutoTilingState.Failed:
            case AutoTilingState.InProgress:
                return sourceImage
            case AutoTilingState.Complete: {
                return this.getResultImage(hints.mode, hints.textureType, imageOpNodeBag)
            }
            default:
                assertNever(state)
        }
    }

    // OperatorBase
    // async queueImageOps(cmdQueue: ImageOpCommandQueue, input: OperatorInput, hints: OperatorProcessingHints): Promise<OperatorOutput> {
    //     if (cmdQueue.mode === "preview" && this.selected && this.$viewMode() === AutoTilingViewMode.Setup) {
    //         return {resultImage: input, options: {stopEvaluation: true}}
    //     }
    //     cmdQueue.beginScope(this.type)
    //     switch (this.$autoTilingState()) {
    //         case AutoTilingState.Setup:
    //         case AutoTilingState.Failed:
    //         case AutoTilingState.InProgress:
    //             cmdQueue.endScope(this.type)
    //             return {resultImage: input}
    //         case AutoTilingState.Complete: {
    //             const resultImage = this.callback.prepareSourceImage(cmdQueue, hints.textureType, await this.getResultImage(cmdQueue, hints.textureType))
    //             cmdQueue.endScope(this.type)
    //             return {resultImage}
    //         }
    //         default:
    //             assertNever(this.$autoTilingState())
    //     }
    // }

    private async getResultImage(mode: OperatorExecutionMode, textureType: TextureType, imageOpNodeBag: ImageOpNodeBag): Promise<AnyImageOpResultNode> {
        if (this.$autoTilingState() !== AutoTilingState.Complete) {
            throw Error("Auto tiling must be complete to get result image")
        }

        const getDataObjectId = async (textureType: TextureType) => {
            let dataObjectId: string | undefined
            if (this.node.outputMaps) {
                dataObjectId = this.node.outputMaps.dataObjectByTextureType[textureType]?.dataObjectId
            } else {
                dataObjectId = this.callback.textureEditorData.textureSetRevisionSpecific.textureSetRevision.mapAssignments.find(
                    (assignment) => assignment.textureType === textureType,
                )?.dataObject.id
            }
            if (!dataObjectId) {
                throw Error(`No data object for texture type: ${textureType}`)
            }
            return dataObjectId
        }

        const dataObjectId = await getDataObjectId(textureType)
        return imageOpNodeBag.getNode("dataObject", ImageOpNodeDataObject, {
            dataObjectId: dataObjectId,
            downloadFormat: mode === "preview" ? "jpg" : "exr",
        })
    }

    private addTilingHints(): void {
        const dataObject = this.callback.textureEditorData?.textureTypeSpecific?.sourceDataObject
        if (!dataObject) {
            throw Error("Data-object not set")
        }
        if (!dataObject.width || !dataObject.height) {
            throw Error("Data-object width/height not set")
        }
        const radius = (dataObject.width + dataObject.height) / 100
        if (this.node.hints.length === 0) {
            const markerWidth: number = 0.6 * dataObject.width
            const x1: number = Math.round(dataObject.width / 2 - markerWidth / 2)
            const x2: number = Math.round(x1 + markerWidth)
            const y: number = Math.round(dataObject.height * 0.15)
            this.node.hints.push({x1, y1: y, x2, y2: y, radius})
        }
        if (this.node.hints.length === 1) {
            const markerHeight: number = 0.6 * dataObject.height
            const y1: number = Math.round(dataObject.height / 2 - markerHeight / 2)
            const y2: number = Math.round(y1 + markerHeight)
            const x: number = Math.round(dataObject.width * 0.15)
            this.node.hints.push({x1: x, y1, x2: x, y2, radius})
        }
    }

    private updateToolbox() {
        this.canvasToolbox?.setItemsEnabled(this.$canChangeAutoTilingSetup())
        this.callback.tilingCanvas.toolbox = this.canvasToolbox
    }

    readonly $canChangeAutoTilingSetup = computed(
        () => this.callback.operators.filter((operator) => !operator.flags.has("is-internal")).length === 1, // only allow changing the setup if no further operators have been applied already
    )

    setViewMode(viewMode: AutoTilingViewMode): void {
        this._$viewMode.set(viewMode)
        this.updateToolbox()
        this.requestEval()
    }

    override async save(processingJobId: string | undefined): Promise<TextureEditNodes.Operator> {
        this.resetEdited()

        if (processingJobId) {
            // check if we're processing the auto-tiling job
            const processingIsAutoTilingJob = this.$autoTilingState() !== AutoTilingState.Complete
            if (processingIsAutoTilingJob) {
                this.node.jobTaskId = processingJobId
            } else {
                // if we're about to process further edits and havn't stored the output maps yet, store them now
                if (!this.node.outputMaps) {
                    const textureSetRevision = this.callback.textureEditorData.textureSetRevisionSpecific.textureSetRevision
                    this.node.outputMaps = {
                        type: "texture-set",
                        dataObjectByTextureType: {},
                    }
                    textureSetRevision.mapAssignments.forEach((mapAssignment) => {
                        this.node.outputMaps!.dataObjectByTextureType[mapAssignment.textureType] = {
                            type: "data-object-reference",
                            dataObjectId: mapAssignment.dataObject.id,
                        }
                    })
                }
            }
        }

        if (this._canvasToolbox.tilingCutoutActive) {
            return this.node
        } else {
            // if tiling cutout is disabled, don't write it to the final node
            const node = deepCopy(this.node)
            node.area = undefined
            return node
        }
    }

    get cutoutArea() {
        return this.node.area
    }

    set cutoutArea(area: Tiling.Area | undefined) {
        this.node.area = area
        this.invalidateTilingResult()
    }

    get tilingHints() {
        return this.node.hints
    }

    set tilingHints(hints: Tiling.Hint[]) {
        this.node.hints = hints
        this.invalidateTilingResult()
    }

    get tilingParams() {
        return this.node.params
    }

    toggleCutoutActive() {
        this.invalidateTilingResult()
        this._canvasToolbox.toggleCutoutActive()
        this._$isTilingCutoutActive.set(this._canvasToolbox.tilingCutoutActive)
    }

    // addTilingHint() {
    //     return this._canvasToolbox.onAddTilingHintClick()
    // }

    // clearTilingHints() {
    //     return this._canvasToolbox.clearTilingHints()
    // }

    invalidateTilingResult() {
        this.markEdited()
        this.node.outputMaps = undefined // reset output maps
        this._$autoTilingState.set(AutoTilingState.Setup) // when we made edits to the auto-tiling operator, we need to return to the setup state
        this._$paramsVersion.update((v) => v + 1)
    }

    // startTilingDetection() {
    //     return this._canvasToolbox.startTiling("detection", undefined, this.callback.textureEditorData.textureSetRevisionSpecific.textureSet.legacyId)
    // }

    // startTilingAlignment() {
    //     return this._canvasToolbox.startTiling("alignment", undefined, this.callback.textureEditorData.textureSetRevisionSpecific.textureSet.legacyId)
    // }

    async createTilingTaskFromParams(organizationLegacyId: number, sourceMapDataObjectIds: Map<TextureType, string>, pxPerCm: number) {
        const graph = await this.tilingService.buildTilingGraph(
            organizationLegacyId,
            "tiling",
            this.node.params,
            this._canvasToolbox.getTilingArea(),
            this.node.hints,
            pxPerCm,
            sourceMapDataObjectIds,
        )
        if (this.node.params.processingMode !== "cloud") {
            throw Error("Tiling task can only be created for cloud tiling.")
        }
        return this.tilingService.createTilingTask(graph, this.callback.organization.legacyId)
    }

    setProcessingMode(value: Tiling.ProcessingMode): void {
        this.node.params.processingMode = value
        this.invalidateTilingResult()
    }

    setFeatureWeighting(value: Tiling.FeatureWeighting): void {
        this.node.params.featureWeighting = value
        this.invalidateTilingResult()
    }

    setBlendingBorder(value: number): void {
        this.node.params.blendingBorder = value
        this.invalidateTilingResult()
    }

    setMaxBlendingRadius(value: number): void {
        this.node.params.maxBlendingRadius = value
        this.invalidateTilingResult()
    }

    setGradientCorrection(value: boolean): void {
        this.node.params.gradientCorrection = value
        this.invalidateTilingResult()
    }

    setFilterMode(value: Tiling.FilterMode): void {
        this.node.params.filterMode = value
        this.invalidateTilingResult()
    }

    setFilterRadius(value: number): void {
        this.node.params.filterRadius = value
        this.invalidateTilingResult()
    }

    setPatternType(value: Tiling.PatternType): void {
        this.node.params.patternType = value
        this.invalidateTilingResult()
    }

    setEdgeAlignmentWindowSize(value: number): void {
        this.node.params.edgeAlignmentWindowSize = value
        this.invalidateTilingResult()
    }

    setEdgeAlignmentSmoothingIterations(value: number): void {
        this.node.params.edgeAlignmentSmoothingIterations = value
        this.invalidateTilingResult()
    }

    setAlignIndividualMaps(value: boolean): void {
        this.node.params.alignIndividualMaps = value
        this.invalidateTilingResult()
    }

    private readonly _$autoTilingState = signal(AutoTilingState.Setup)
    readonly $autoTilingState = this._$autoTilingState.asReadonly()
    private readonly _$viewMode = signal(AutoTilingViewMode.Setup)
    readonly $viewMode = this._$viewMode.asReadonly()
    private readonly _$paramsVersion = signal(0)
    readonly $paramsVersion = this._$paramsVersion.asReadonly()
    private readonly _$isTilingCutoutActive = signal(false)
    readonly $isTilingCutoutActive = this._$isTilingCutoutActive.asReadonly()

    private tilingService: Tiling.AutoTilingService
    private _canvasToolbox: TilingAutoGenerateToolbox
}
